import merge from 'lodash/merge'
import omit from 'lodash/omit'
import { DataLayerObject, DiscountData, SmdlAssets, AssetItem } from '../types'

/**
 * Builds module items from data attributes:
 * data-type
 * data-position
 */
const buildItem = (el: HTMLElement) => ({
  id: el.dataset.itemId,
  type: el.getAttribute('data-type'),
  position: el.getAttribute('data-position'),
})

/**
 * Used for asset details
 * TODO: unsure if this will need populating for promo banners at this time https://smarty-mobile.atlassian.net/browse/DIGDEV-11549
 */
const buildDiscount = (discount?: DiscountData) => {
  if (discount) {
    return { discount: { lines: [discount] } }
  }
  return undefined
}

/**
 * Item array for module assets
 * TODO: unsure if this will need populating for promo banners at this time https://smarty-mobile.atlassian.net/browse/DIGDEV-11549
 */
const buildItemWithAssetData = (el: HTMLElement) => {
  const { asset, discount } = convertDataAttributeToObjects(el)
  const {
    value: { displayGross, displayTax },
  } = asset || { value: {} }

  return {
    ...asset,
    ...(asset?.discount && {
      discount: {
        ...asset?.discount,
        value: {
          displayGross: Number(asset.discount.value.displayGross),
        },
      },
    }),
    ...buildDiscount(discount),
    value: {
      ...asset?.value,
      ...(asset?.value && {
        displayGross: Number(displayGross),
        displayTax: Number(displayTax),
      }),
    },
    type: el.getAttribute('data-asset-type'),
    id: el.getAttribute('data-asset-id'),
  }
}

type NestedRecord = { [k: string]: string | NestedRecord | undefined }
/**
 *
 */
export const toObjectValue = ([name, value]: [string, string | undefined]):
  | NestedRecord
  | string
  | undefined => {
  const keys = name.split('_')
  return keys
    .reverse()
    .reduce(
      (acc: NestedRecord | string | undefined, key: string) => ({ [key]: acc }),
      value
    )
}

/**
 *
 */
export const convertDataAttributeToObjects = (
  el: HTMLElement
): DataLayerObject =>
  Object.entries(el.dataset).reduce(
    (acc, val) => merge(toObjectValue(val), acc),
    {}
  )

/**
 * Uses element ID to create an array of module elements
 * */
const createArrayFromID = (
  parentId: string,
  selector: string
): HTMLElement[] | null => {
  const parentElement = document.getElementById(parentId)

  if (!parentElement) {
    return null
  }

  return [...(parentElement?.querySelectorAll(selector) || [])] as HTMLElement[]
}

/**
 * Uses selected element to create an array of module items & asset items
 * */
const createArrayFromElement = (
  parentElement: Document | HTMLElement,
  selector: string
): HTMLElement[] =>
  [...(parentElement?.querySelectorAll(selector) || [])] as HTMLElement[]

const findModuleItems = (module: Document | HTMLElement) =>
  createArrayFromElement(module, '[data-item-id]').map(buildItem)

/**
 * Searches through the document for data-module-id and maps them
 * in an array in the data layer.
 */
export const findModules = (parentElement: string) => {
  const moduleArray = createArrayFromID(parentElement, '[data-module-id]')
  if (!moduleArray) {
    return null
  }
  return moduleArray?.map((element) => ({
    ...convertDataAttributeToObjects(element).module,
    id: element.getAttribute('data-module-id'),
    items: findModuleItems(element),
  }))
}

/**
 * Searches through the document for data-asset-id and maps them
 * in an array in the data layer.
 */
export const findAssets = (parentElement: string): SmdlAssets | null => {
  const assetArray = createArrayFromID(parentElement, '[data-asset-id]')
  if (!assetArray) {
    return null
  }
  return assetArray.map(buildItemWithAssetData).reduce((acc, item) => {
    if (!acc[item.type || 'unknown']) {
      acc[item.type || 'unknown'] = []
    }
    acc[item.type || 'unknown'].push(omit(item, ['type']))
    return acc
  }, {} as AssetItem)
}
